import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { DataTableContainer } from '../../components/DataTable';
import { PageContainer } from '../../components';
import { useNotification } from '../../hooks/useNotification';
import { useActiveReports } from '../../hooks/useActiveReports';
import { useReportManagement } from '../../hooks/useReportManagement';
import { useReportManagementPermissions } from '../../hooks';
import { TABLE_COLUMNS, FILTER_CONFIGS, SEARCH_CONFIGS } from '../../constants/tableConfigurations';
import ReportBoardView from './components/ReportBoardView';
import CreateFlowDialog from './components/CreateFlowDialog';
import UpdateReportStatusDialog from './components/UpdateReportStatusDialog';

const ReportManagementPage = ({ view = 'list' }) => {
  const { t } = useTranslation();

  const { allReports = [], loading, refetch } = useActiveReports();
  const {
    flows = [],
    fetchFlows,
    isRoot,
    requestFinancialResponsible,
  } = useReportManagement();
  const { showNotification } = useNotification();
  const permissions = useReportManagementPermissions();
  const canView = permissions?.canView || isRoot;
  const canCreate = permissions?.canCreate || isRoot;
  const canEdit = permissions?.canEdit || isRoot;
  const canDownload = permissions?.canDownload || isRoot;

  const [selectedFlowId, setSelectedFlowId] = useState('');
  const [createFlowOpen, setCreateFlowOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [statusUpdateRow, setStatusUpdateRow] = useState(null);
  const [financialRequestRow, setFinancialRequestRow] = useState(null);
  const [financialRequestSubmitting, setFinancialRequestSubmitting] = useState(false);

  useEffect(() => {
    if (view === 'flows' && flows.length > 0 && !selectedFlowId) {
      const firstActive = flows.find((f) => !f.is_archived);
      if (firstActive) setSelectedFlowId(firstActive.id);
    }
  }, [view, flows, selectedFlowId]);

  const columns = TABLE_COLUMNS.reportManagement;
  const searchConfig = SEARCH_CONFIGS.reportManagement;
  const filterConfigs = FILTER_CONFIGS.reportManagement;

  const tableData = useMemo(() => {
    const statusLabels = {
      PENDING: t('reportManagement.statuses.PENDING', { defaultValue: 'Bekliyor' }),
      IN_PROGRESS: t('reportManagement.statuses.IN_PROGRESS', { defaultValue: 'Devam Ediyor' }),
      COMPLETED: t('reportManagement.statuses.COMPLETED', { defaultValue: 'Tamamlandı' }),
      OVERDUE: t('reportManagement.statuses.OVERDUE', { defaultValue: 'Gecikmiş' }),
    };
    const jobTypeLabels = {
      AR_GE: t('jobs.jobTypes.AR_GE', { defaultValue: 'AR-GE Merkezi' }),
      TASARIM_MERKEZI: t('jobs.jobTypes.TASARIM_MERKEZI', { defaultValue: 'Tasarım Merkezi' }),
      TEKNOPARK: t('jobs.jobTypes.TEKNOPARK', { defaultValue: 'Teknokent' }),
      TUBITAK: t('jobs.jobTypes.TUBITAK', { defaultValue: 'TÜBİTAK' }),
      ULUSLARARASI: t('jobs.jobTypes.ULUSLARARASI', { defaultValue: 'Uluslararası' }),
    };
    const enriched = allReports.map((r) => ({
      ...r,
      statusLabel: statusLabels[r.status] || r.status || '-',
      jobTypeLabel: jobTypeLabels[r.jobType] || r.jobType || '-',
    }));
    return showCompleted ? enriched : enriched.filter((r) => r.status !== 'COMPLETED');
  }, [allReports, t, showCompleted]);

  const allTags = useMemo(() => {
    const tagSet = new Set();
    allReports.forEach((r) => {
      if (Array.isArray(r.reportTags)) {
        r.reportTags.forEach((tag) => tagSet.add(tag));
      }
    });
    return [...tagSet].sort().map((tag) => ({ value: tag, label: tag }));
  }, [allReports]);

  const inFlowOptions = useMemo(() => ([
    { value: 'true', label: t('common.yes', { defaultValue: 'Evet' }) },
    { value: 'false', label: t('common.no', { defaultValue: 'Hayır' }) },
  ]), [t]);

  const enrichedFilterConfigs = useMemo(() =>
    filterConfigs.map((f) => {
      if (f.field === 'reportTags') return { ...f, options: allTags };
      if (f.field === 'inFlowLabel') return { ...f, options: inFlowOptions };
      return f;
    }),
  [filterConfigs, allTags, inFlowOptions]);

  const rowActions = useCallback((row) => {
    const actions = [];

    // "Statüyü Güncelle" requires edit permission
    if (canEdit) {
      actions.push({
        type: 'updateStatus',
        label: t('reportManagement.actions.updateStatus', { defaultValue: 'Statüyü Güncelle' }),
        icon: <EditIcon fontSize="small" />,
        color: 'primary',
      });
    }

    // "Mali Sorumlu Talep Et" sadece atanmış mali sorumlu yoksa ve
    // bu (job, report) çifti için bekleyen talep yoksa görünür.
    // Talep oluşturulduğu için canCreate gerekir.
    if (canCreate && !row?.financialUserId && !row?.financialRequestPending) {
      actions.push({
        type: 'requestFinancial',
        label: t('reportManagement.actions.requestFinancial', { defaultValue: 'Mali Sorumlu Talep Et' }),
        icon: <PersonAddIcon fontSize="small" />,
        color: 'warning',
      });
    }

    return actions;
  }, [t, canEdit, canCreate]);

  const handleRowAction = useCallback((actionType, row) => {
    if (actionType === 'updateStatus') {
      setStatusUpdateRow(row);
      return true;
    }
    if (actionType === 'requestFinancial') {
      setFinancialRequestRow(row);
      return true;
    }
    return false;
  }, []);

  const handleStatusUpdateSuccess = useCallback(() => {
    setStatusUpdateRow(null);
    refetch?.();
  }, [refetch]);

  const handleConfirmFinancialRequest = useCallback(async () => {
    if (!financialRequestRow) return;
    setFinancialRequestSubmitting(true);
    try {
      await requestFinancialResponsible(
        financialRequestRow.jobId,
        financialRequestRow.reportId
      );
      showNotification(
        t('reportManagement.requestFinancial.success', { defaultValue: 'Mali sorumlu talep edildi' }),
        'success'
      );
      setFinancialRequestRow(null);
      refetch?.();
    } catch (err) {
      // Bekleyen bir talep zaten varsa Postgres unique index hatası fırlatır.
      const msg = err?.message || '';
      const isDuplicate = msg.includes('report_financial_requests_unique_pending');
      showNotification(
        isDuplicate
          ? t('reportManagement.requestFinancial.alreadyPending', { defaultValue: 'Bu rapor için zaten bekleyen bir talep var' })
          : t('reportManagement.requestFinancial.error', { defaultValue: 'Talep oluşturulamadı' }),
        'error'
      );
    } finally {
      setFinancialRequestSubmitting(false);
    }
  }, [financialRequestRow, requestFinancialResponsible, refetch, showNotification, t]);


  if (view === 'list') {
    return (
      <PageContainer currentPage={t('reportManagement.pageTitle', { defaultValue: 'Raporlar' })}>
        <DataTableContainer
          title={t('reportManagement.pageTitle', { defaultValue: 'Raporlar' })}
          columns={columns}
          data={tableData}
          loading={loading}
          showSearch={canView}
          searchFields={searchConfig.fields}
          searchPlaceholder={searchConfig.placeholder}
          showFilters={true}
          filterConfigs={enrichedFilterConfigs}
          enableAutoFilter={true}
          showPagination={true}
          rowsPerPage={20}
          showAddButton={false}
          exportButton={canDownload}
          exportFilename="rapor-listesi"
          rowActions={rowActions}
          onRowAction={handleRowAction}
          headerExtraContent={
            <FormControlLabel
              control={
                <Switch
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                  size="small"
                />
              }
              label={t('reportManagement.toggle.showCompleted', { defaultValue: 'Tamamlananları göster' })}
              sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
            />
          }
        />
        <UpdateReportStatusDialog
          open={Boolean(statusUpdateRow)}
          report={statusUpdateRow}
          onClose={() => setStatusUpdateRow(null)}
          onSuccess={handleStatusUpdateSuccess}
        />
        <Dialog
          open={Boolean(financialRequestRow)}
          onClose={() => !financialRequestSubmitting && setFinancialRequestRow(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {t('reportManagement.actions.requestFinancial', { defaultValue: 'Mali Sorumlu Talep Et' })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('reportManagement.requestFinancial.confirm', {
                defaultValue:
                  '{{firm}} - {{job}} işi için mali sorumlu talebi oluşturulsun mu?',
                firm: financialRequestRow?.firmName || '-',
                job: financialRequestRow?.jobTitle || '-',
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setFinancialRequestRow(null)}
              disabled={financialRequestSubmitting}
              color="inherit"
            >
              {t('common.cancel', { defaultValue: 'İptal' })}
            </Button>
            <Button
              onClick={handleConfirmFinancialRequest}
              disabled={financialRequestSubmitting}
              variant="contained"
              color="warning"
            >
              {t('common.confirm', { defaultValue: 'Onayla' })}
            </Button>
          </DialogActions>
        </Dialog>
      </PageContainer>
    );
  }


  return (
    <Box sx={{ p: 5 }}>
      <ReportBoardView
        flows={flows}
        selectedFlowId={selectedFlowId}
        onFlowSelect={setSelectedFlowId}
        isRoot={isRoot}
        canCreate={canCreate}
        canEdit={canEdit}
        onCreateFlow={() => setCreateFlowOpen(true)}
        onRefresh={fetchFlows}
        onReportsChanged={refetch}
      />

      <CreateFlowDialog
        open={createFlowOpen}
        onClose={() => setCreateFlowOpen(false)}
        onSuccess={() => {
          setCreateFlowOpen(false);
          if (fetchFlows) fetchFlows();
        }}
      />
    </Box>
  );
};

export default ReportManagementPage;
